//
import PropTypes from "prop-types"

import { createOrderByFilterGroup, genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"
import { createCustomFilterGroup } from "shared/search/containers/FilterGroup/CustomFilterGroup"
import {
    createListFilter,
    createNotInListFilter,
    ManuallyChangedInListFilter,
    StanceFilter,
    PriorityFilter,
    PriorityLastUserFilter,
    StanceLastUserFilter,
    RegionNoFederalFilter,
    ReviewedByMeFilter,
    ReviewedByOrgFilter,
    ReviewedByTeamFilter,
    ReviewedByUserFilter,
    NotReviewedByUserFilter,
} from "shared/search/containers/Filter/Filters"
import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import {
    GlobalStatusFilter,
    GeneralStatusFilter,
    DetailedStatusFilter,
    TypeFilter,
    RegulationDateListNoFutureFilter,
    RegulationDatePickerStartNoFutureFilter,
    RegulationDatePickerEndNoFutureFilter,
    RegulationDateListPreProposedNoFutureFilter,
    RegulationPreProposedDatePickerStartNoFutureFilter,
    RegulationPreProposedDatePickerEndNoFutureFilter,
    RegulationDateListProposedNoFutureFilter,
    RegulationProposedDatePickerStartNoFutureFilter,
    RegulationProposedDatePickerEndNoFutureFilter,
    RegulationDateListCommentNoFutureFilter,
    RegulationCommentDatePickerStartNoFutureFilter,
    RegulationCommentDatePickerEndNoFutureFilter,
    RegulationDateListEffectiveNoFutureFilter,
    RegulationEffectiveDatePickerStartNoFutureFilter,
    RegulationEffectiveDatePickerEndNoFutureFilter,
    RegulationDateListExpirationNoFutureFilter,
    RegulationExpirationDatePickerStartNoFutureFilter,
    RegulationExpirationDatePickerEndNoFutureFilter,
    RegulationDateListCurrentStatusNoFutureFilter,
    RegulationCurrentStatusDatePickerStartNoFutureFilter,
    RegulationCurrentStatusDatePickerEndNoFutureFilter,
    RegulationTrackingDashboardFilter,
    RegulationTitleFilter,
    RegulationDescriptionFilter,
    RegulationAuthorityFilter,
    RegulationCitationFilter,
    RegulationAgencySourceFilter,
    RegulationAgencyPublicOrgSourceFilter,
    RegulationDocketSourceFilter,
    RegulationEO12866Filter,
    RegulationEO13771Filter,
    RegulationProposalYearFilter,
    GlobalRegionFilter,
} from "shared/search/containers/Filter/RegulationFilters"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

const defaultPropTypes = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

export const RegulationCustomFilterGroup = createCustomFilterGroup(
    "regulationCustomFilterGroup",
    DjangIO.app.models.QuorumDataType.regulation.value,
)

export const RegulationTypeFilterGroup = (props) => genericFilterGroup(props)
RegulationTypeFilterGroup.propTypes = defaultPropTypes
RegulationTypeFilterGroup.defaultProps = {
    filters: [TypeFilter, RegulationEO12866Filter, RegulationEO13771Filter],
    uniqueFilterGroupId: "regulationTypeFilterGroup",
    displayLabel: "Regulation Type",
    helpText: "Filter by regulatory type.",
}

export const RegulationAgencyFilterGroup = (props) => genericFilterGroup(props)
RegulationAgencyFilterGroup.propTypes = defaultPropTypes
RegulationAgencyFilterGroup.defaultProps = {
    filters: [RegulationAgencySourceFilter, RegulationAgencyPublicOrgSourceFilter],
    uniqueFilterGroupId: "regulationAgencyFilterGroup",
    displayLabel: Userdata.has_international_region ? "Department" : "Agency",
    helpText: Userdata.has_international_region
        ? "Filter by the Department that the regulation is associated with."
        : "Filter by the Agency that the regulation is associated with.",
}

export const RegulationDocketFilterGroup = (props) => genericFilterGroup(props)
RegulationDocketFilterGroup.propTypes = defaultPropTypes
RegulationDocketFilterGroup.defaultProps = {
    filters: [RegulationDocketSourceFilter],
    uniqueFilterGroupId: "regulationDocketFilterGroup",
    displayLabel: "Docket",
    helpText: "Filter by the Docket that the regulation is associated with.",
}

export const RegulationStatusFilterGroup = (props) => genericFilterGroup(props)
RegulationStatusFilterGroup.propTypes = defaultPropTypes
RegulationStatusFilterGroup.defaultProps = {
    filters: [
        ...(Userdata.isGlobalMode() ? [GlobalStatusFilter] : [GeneralStatusFilter]),
        ...(Userdata.has_international_region ? [] : [DetailedStatusFilter]),
    ],
    uniqueFilterGroupId: "regulationStatusFilterGroup",
    displayLabel: "Current Status",
    helpText: "Filter by regulatory status",
}

export const RegulationSearchFilterGroup = (props) => genericFilterGroup(props)
RegulationSearchFilterGroup.propTypes = defaultPropTypes
RegulationSearchFilterGroup.defaultProps = {
    filters: [RegulationTitleFilter, RegulationDescriptionFilter],
    uniqueFilterGroupId: "regulationTitleFilterGroup",
    displayLabel: "Title & Description",
    helpText: "Search for regulations by their title or description.",
}

export const RegulationAuthorityCitationSearchFilterGroup = (props) => genericFilterGroup(props)
RegulationAuthorityCitationSearchFilterGroup.propTypes = defaultPropTypes
RegulationAuthorityCitationSearchFilterGroup.defaultProps = {
    filters: [RegulationAuthorityFilter, RegulationCitationFilter],
    uniqueFilterGroupId: "regulationAuthorityCitationSearchFilterGroup",
    displayLabel: "Authority & Citation",
    helpText: "Search for regulations by their authority or citation.",
}

export const RegulationRegionFilterGroup = (props) => genericFilterGroup(props)
RegulationRegionFilterGroup.propTypes = defaultPropTypes
RegulationRegionFilterGroup.defaultProps = {
    filters: Userdata.isGlobalMode() ? [GlobalRegionFilter] : [RegionNoFederalFilter],
    uniqueFilterGroupId: "regulationRegionFilterGroup",
    displayLabel: "Region",
    helpText: "Filter by regulatory region.",
}

export const RegulationListFilterGroup = (props) => genericFilterGroup(props)
RegulationListFilterGroup.propTypes = defaultPropTypes
RegulationListFilterGroup.defaultProps = {
    filters: [
        createListFilter(DjangIO.app.models.QuorumDataType.regulation.value),
        createNotInListFilter(DjangIO.app.models.QuorumDataType.regulation.value),
        ManuallyChangedInListFilter,
    ],
    uniqueFilterGroupId: "regulationListFilterGroup",
    displayLabel: "Lists",
    helpText: "Filter regulations by your lists to show regulations you've saved to Projects.",
}

export const RegulationStanceFilterGroup = (props) => genericFilterGroup(props)
RegulationStanceFilterGroup.propTypes = defaultPropTypes
RegulationStanceFilterGroup.defaultProps = {
    filters: [StanceFilter, StanceLastUserFilter],
    uniqueFilterGroupId: "regulationStanceFilterGroup",
    displayLabel: "Stance",
    helpText: "Filter regulations by your organization's stance.",
}

export const RegulationPriorityFilterGroup = (props) => genericFilterGroup(props)
RegulationPriorityFilterGroup.propTypes = defaultPropTypes
RegulationPriorityFilterGroup.defaultProps = {
    filters: [PriorityFilter, PriorityLastUserFilter],
    uniqueFilterGroupId: "regulationPriorityFilterGroup",
    displayLabel: "Priority",
    helpText: "Filter regulations by your organization's indicated priority.",
}

export const RegulationDateFilterGroup = (props) => genericFilterGroup(props)
RegulationDateFilterGroup.propTypes = defaultPropTypes
RegulationDateFilterGroup.defaultProps = {
    filters: [
        RegulationDateListNoFutureFilter,
        RegulationDatePickerStartNoFutureFilter,
        RegulationDatePickerEndNoFutureFilter,

        ...(Userdata.isGlobalMode()
            ? []
            : [
                  RegulationDateListPreProposedNoFutureFilter,
                  RegulationPreProposedDatePickerStartNoFutureFilter,
                  RegulationPreProposedDatePickerEndNoFutureFilter,
              ]),

        RegulationDateListProposedNoFutureFilter,
        RegulationProposedDatePickerStartNoFutureFilter,
        RegulationProposedDatePickerEndNoFutureFilter,

        ...(Userdata.isGlobalMode()
            ? []
            : [
                  RegulationDateListCommentNoFutureFilter,
                  RegulationCommentDatePickerStartNoFutureFilter,
                  RegulationCommentDatePickerEndNoFutureFilter,
              ]),

        RegulationDateListEffectiveNoFutureFilter,
        RegulationEffectiveDatePickerStartNoFutureFilter,
        RegulationEffectiveDatePickerEndNoFutureFilter,

        ...(Userdata.isGlobalMode()
            ? []
            : [
                  RegulationDateListExpirationNoFutureFilter,
                  RegulationExpirationDatePickerStartNoFutureFilter,
                  RegulationExpirationDatePickerEndNoFutureFilter,
              ]),

        RegulationDateListCurrentStatusNoFutureFilter,
        RegulationCurrentStatusDatePickerStartNoFutureFilter,
        RegulationCurrentStatusDatePickerEndNoFutureFilter,
    ],
    uniqueFilterGroupId: "regulationDateCreatedFilterGroup",
    displayLabel: "Date",
    helpText: "Filter for regulations by date type",
}

export const RegulationProjectFilterGroup = (props) => genericFilterGroup(props)
RegulationProjectFilterGroup.propTypes = defaultPropTypes
RegulationProjectFilterGroup.defaultProps = {
    filters: [inIssueFilter, notInIssueFilter],
    uniqueFilterGroupId: "regulationProjectFilterGroup",
    displayLabel: "Issues",
    helpText: "Filter regulations by your tagged Issues.",
}

export const RegulationTrackingDashboardFilterGroup = (props) => genericFilterGroup(props)
RegulationTrackingDashboardFilterGroup.propTypes = defaultPropTypes
RegulationTrackingDashboardFilterGroup.defaultProps = {
    filters: [RegulationTrackingDashboardFilter],
    uniqueFilterGroupId: "regulationTrackingDashboardFilterGroup",
    displayLabel: "Tracking Boards",
    helpText: "Filter for results that currently appear on the unreviewed regulations tab in your tracking board(s).",
}
export const RegulationOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "regulationOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.regulation.value,
})

export const RegulationReviewFilterGroup = (props) => genericFilterGroup(props)
RegulationReviewFilterGroup.propTypes = defaultPropTypes
RegulationReviewFilterGroup.defaultProps = {
    filters: [
        ReviewedByMeFilter,
        ReviewedByOrgFilter,
        ReviewedByUserFilter,
        ReviewedByTeamFilter,
        NotReviewedByUserFilter,
    ],
    uniqueFilterGroupId: "regulationReviewFilterGroup",
    displayLabel: "Review Status",
    helpText: "Filter results by review status of regulation",
}

export const RegulationProposalYearFilterGroup = (props) => genericFilterGroup(props)
RegulationProposalYearFilterGroup.propTypes = defaultPropTypes
RegulationProposalYearFilterGroup.defaultProps = {
    filters: [RegulationProposalYearFilter],
    uniqueFilterGroupId: "regulationProposalYearFilterGroup",
    displayLabel: "Proposal Year",
    invisible: true,
}
